import React, { useEffect, useState } from "react";
import styled from "styled-components";

const pageTitle = "Memory Match 6x6";
const CARD_CODES = [
  "2C","3C","4C","5C","6C","7C","8C","9C","0C","JC","QC","KC","AC",
  "2D","3D","4D","5D","6D","7D","8D","9D","0D","JD","QD","KD","AD",
  "2H","3H","4H","5H","6H","7H","8H","9H","0H","JH","QH","KH","AH",
  "2S","3S","4S","5S","6S","7S","8S","9S","0S","JS","QS","KS","AS",
];

const cardImageUrl = (code) => `https://deckofcardsapi.com/static/img/${code}.png`;

const shuffle = (items) => {
  const array = [...items];
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

const buildDeck = () => {
  const selectedCodes = shuffle(CARD_CODES).slice(0, 18);
  const standardPairs = selectedCodes.map((code) => ({
    pairId: code,
    image: cardImageUrl(code),
  }));

  return shuffle(
    standardPairs.flatMap((card) => [
      { id: `${card.pairId}-a`, ...card },
      { id: `${card.pairId}-b`, ...card },
    ])
  );
};

const Container = styled.div`
  min-height: 100vh;
  padding: 30px 20px 60px;
  background: radial-gradient(circle at top left, rgba(79, 70, 229, 0.14), transparent 35%),
    linear-gradient(180deg, #070b20 0%, #0f183f 100%);
  color: ${({ theme }) => theme.text_primary};
`;

const Header = styled.div`
  max-width: 1120px;
  margin: 0 auto 26px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 40px;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.primary};
`;

const Subtitle = styled.p`
  max-width: 860px;
  line-height: 1.7;
  color: ${({ theme }) => theme.text_secondary};
  @media only screen and (max-width: 640px) {
    font-size: 14px;
  }
`;

const StatusRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
  @media only screen and (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 18px;
  border-radius: 16px;
  min-height: 110px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.text_secondary};
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.text_primary};
`;

const Board = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 24px auto 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 6px;
`;

const CardButton = styled.button`
  width: 100%;
  max-height: 90px;
  aspect-ratio: 5 / 7;
  border-radius: 12px;
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  background: ${({ flipped, theme }) =>
    flipped ? theme.bg : "linear-gradient(135deg, rgba(79,70,229,0.9), rgba(14, 165, 233, 0.9))"};
  cursor: pointer;
  overflow: hidden;
  display: grid;
  place-items: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.14);
  }
  &:disabled {
    cursor: default;
    opacity: 0.85;
  }
  @media only screen and (max-width: 640px) {
    max-height: 90px;
  }
  @media only screen and (max-width: 480px) {
    max-height: 78px;
  }
`;

const CardInner = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
`;

const CardBack = styled.div`
  width: 80%;
  height: 80%;
  border-radius: 14px;
  background: radial-gradient(circle at top left, rgba(255, 255, 255, 0.15), transparent 46%),
    linear-gradient(135deg, rgba(79, 70, 229, 1), rgba(14, 165, 233, 1));
  color: #fff;
  font-size: 28px;
  font-weight: 700;
`;

const CardFace = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ButtonRow = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const ActionButton = styled.button`
  border: none;
  cursor: pointer;
  padding: 14px 24px;
  border-radius: 12px;
  font-weight: 700;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.white};
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 0.92;
  }
`;

const Input = styled.input`
  width: 300px;
  max-width: 100%;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: ${({ theme }) => theme.bg};
  color: ${({ theme }) => theme.text_primary};
`;

const Scoreboard = styled.div`
  max-width: 1120px;
  margin: 34px auto 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
`;

const ScoreCard = styled.div`
  background: ${({ theme }) => theme.card};
  padding: 18px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ScoreTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
  margin-bottom: 8px;
`;

const ScoreText = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.text_secondary};
`;

const createScoreKey = () => "memoryGameHighScores";

const MemoryGame = () => {
  const [playerName, setPlayerName] = useState("");
  const [started, setStarted] = useState(false);
  const [cards, setCards] = useState([]);
  const [firstChoice, setFirstChoice] = useState(null);
  const [secondChoice, setSecondChoice] = useState(null);
  const [flippedIds, setFlippedIds] = useState([]);
  const [matchedIds, setMatchedIds] = useState([]);
  const [mistakes, setMistakes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [highScores, setHighScores] = useState([]);

  const loadScores = () => {
    try {
      const item = window.localStorage.getItem(createScoreKey());
      return item ? JSON.parse(item) : [];
    } catch (error) {
      return [];
    }
  };

  const resetGame = () => {
    setCards(shuffle(buildDeck()));
    setFirstChoice(null);
    setSecondChoice(null);
    setFlippedIds([]);
    setMatchedIds([]);
    setMistakes(0);
    setSeconds(0);
    setCompleted(false);
    setDisabled(false);
  };

  useEffect(() => {
    setHighScores(loadScores());
  }, []);

  useEffect(() => {
    if (!started || completed) return undefined;
    const timer = setInterval(() => {
      setSeconds((value) => value + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [started, completed]);

  useEffect(() => {
    if (firstChoice && secondChoice) {
      setDisabled(true);
      if (firstChoice.pairId === secondChoice.pairId) {
        setMatchedIds((prev) => [...prev, firstChoice.id, secondChoice.id]);
        setTimeout(() => {
          setFirstChoice(null);
          setSecondChoice(null);
          setDisabled(false);
        }, 600);
      } else {
        setMistakes((value) => value + 1);
        setTimeout(() => {
          setFlippedIds((prev) => prev.filter((id) => id !== firstChoice.id && id !== secondChoice.id));
          setFirstChoice(null);
          setSecondChoice(null);
          setDisabled(false);
        }, 900);
      }
    }
  }, [firstChoice, secondChoice]);

  useEffect(() => {
    if (!started || completed || cards.length === 0) return;
    if (matchedIds.length === cards.length) {
      setCompleted(true);
      const newScore = {
        player: playerName || "Player",
        time: seconds,
        errors: mistakes,
        date: new Date().toISOString(),
      };
      const updated = [newScore, ...highScores]
        .sort((a, b) => {
          if (a.time !== b.time) return a.time - b.time;
          return a.errors - b.errors;
        })
        .slice(0, 3);
      setHighScores(updated);
      window.localStorage.setItem(createScoreKey(), JSON.stringify(updated));
    }
  }, [matchedIds, cards, started, seconds, mistakes, playerName, highScores, completed]);

  const handleStart = () => {
    if (!playerName.trim()) return;
    resetGame();
    setStarted(true);
  };

  const handleSelect = (card) => {
    if (disabled || flippedIds.includes(card.id) || matchedIds.includes(card.id)) return;
    if (!started) return;
    if (!firstChoice) {
      setFirstChoice(card);
      setFlippedIds((prev) => [...prev, card.id]);
      return;
    }
    if (firstChoice && !secondChoice && firstChoice.id !== card.id) {
      setSecondChoice(card);
      setFlippedIds((prev) => [...prev, card.id]);
    }
  };

  const matchedPairs = matchedIds.length / 2;
  const totalPairs = cards.length / 2;
  const formattedTime = `${Math.floor(seconds / 60).toString().padStart(2, "0")}:${(seconds % 60).toString().padStart(2, "0")}`;

  const scoreDescription = (score) => {
    const date = new Date(score.date);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  };

  const bestScore = highScores?.[0];

  return (
    <Container>
      <Header>
        <Title>{pageTitle}</Title>
        <Subtitle>
          Start a personalized 8x8 memory challenge with a deck built from classic playing cards and six unique joker pairs. Match the board, track your mistakes, save the top 3 best sessions, and see your name on the leader board.
        </Subtitle>
        {!started ? (
          <div>
            <Input
              placeholder="Enter your name to begin"
              value={playerName}
              onChange={(event) => setPlayerName(event.target.value)}
            />
            <ButtonRow>
              <ActionButton onClick={handleStart}>Start the match</ActionButton>
            </ButtonRow>
          </div>
        ) : (
          <ButtonRow>
            <ActionButton onClick={resetGame}>Restart Game</ActionButton>
          </ButtonRow>
        )}
      </Header>

      {started && (
        <>
          <StatusRow>
            <StatCard>
              <StatLabel>Player</StatLabel>
              <StatValue>{playerName || "Player"}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Time</StatLabel>
              <StatValue>{formattedTime}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Mistakes</StatLabel>
              <StatValue>{mistakes}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Matched Pairs</StatLabel>
              <StatValue>{matchedPairs} / {totalPairs}</StatValue>
            </StatCard>
          </StatusRow>

          <Board>
            {cards.map((card) => {
              const isFlipped = flippedIds.includes(card.id) || matchedIds.includes(card.id);
              return (
                <CardButton
                  key={card.id}
                  flipped={isFlipped}
                  onClick={() => handleSelect(card)}
                  disabled={matchedIds.includes(card.id)}
                >
                  <CardInner>
                    {isFlipped ? (
                      <CardFace src={card.image} alt="playing card" />
                    ) : (
                      <CardBack>?</CardBack>
                    )}
                  </CardInner>
                </CardButton>
              );
            })}
          </Board>

          <Scoreboard>
            {bestScore && (
              <ScoreCard>
                <ScoreTitle>Best Score</ScoreTitle>
                <ScoreText>
                  {`${bestScore.player} — ${Math.floor(bestScore.time / 60).toString().padStart(2, "0")}:${(bestScore.time % 60).toString().padStart(2, "0")} with ${bestScore.errors} error${bestScore.errors === 1 ? "" : "s"}`}
                </ScoreText>
                <ScoreText>{scoreDescription(bestScore)}</ScoreText>
              </ScoreCard>
            )}
            <ScoreCard>
              <ScoreTitle>What makes it fun</ScoreTitle>
              <ScoreText>
                This memory board uses a responsive 6x6 layout and 18 shuffled card pairs, so each match is fast and feels fresh on desktop or phone.
              </ScoreText>
            </ScoreCard>
            {completed && (
              <ScoreCard>
                <ScoreTitle>Finish line</ScoreTitle>
                <ScoreText>
                  {`Nice job, ${playerName || "Player"}! You matched all 36 cards in ${formattedTime} with ${mistakes} mistake${mistakes === 1 ? "" : "s"}. Your session has been saved to the memory hall of fame.`}
                </ScoreText>
              </ScoreCard>
            )}
            <ScoreCard>
              <ScoreTitle>Top 3 Scores</ScoreTitle>
              {highScores.length ? (
                highScores.map((score, index) => (
                  <ScoreText key={score.date}>
                    {index + 1}. {score.player} — {`${Math.floor(score.time / 60).toString().padStart(2, "0")}:${(score.time % 60).toString().padStart(2, "0")}`} / {score.errors} error{score.errors === 1 ? "" : "s"} / {scoreDescription(score)}
                  </ScoreText>
                ))
              ) : (
                <ScoreText>No score saved yet. Finish a round to seed the top 3.</ScoreText>
              )}
            </ScoreCard>
          </Scoreboard>
        </>
      )}
    </Container>
  );
};

export default MemoryGame;
